import React, { useState } from 'react'
import { Star, Wrench, Check, ArrowRight, Calendar, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'

const OctoberFeaturedSection: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<'basic' | 'full'>('basic')
  const accentColor = '#AB9381'
  const bgCream = 'rgb(246, 241, 237)'
  const heading = 'rgb(15,15,15)'
  const sub = 'rgb(76,76,75)'
  const pillBg = 'rgb(237, 227, 217)'

  const basicFeatures = [
    'Professional dryer vent cleaning',
    'Inspection of dryer vent system',
    'Removal of lint and debris',
    'Safety check of vent connections',
    'Basic maintenance report'
  ]

  const fullFeatures = [
    'Everything in Basic Package',
    'HVAC Filters cleaned/replaced',
    'Service CO & smoke detectors',
    'Test gas fireplace',
    'Inspect dishwasher components',
    'Run dishwasher cleaning cycle',
    'Check refrigerator and freezer temps',
    'Inspect and clean dryer vent and hose',
    'Clean gutters',
    'Complete home safety inspection',
    'Detailed maintenance report',
    'Priority scheduling for future services'
  ]

  return (
    <section className="py-16" style={{ backgroundColor: bgCream }}>
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: pillBg, color: heading }}>
            <Star className="h-3 w-3" />
            October Featured Service
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold mb-4" style={{ color: heading }}>
            Essential Dryer Vent Maintenance
          </h2>
          <p className="text-lg max-w-3xl mx-auto" style={{ color: sub }}>
            Keep your home safe and efficient with our professional dryer vent cleaning service. 
            Upgrade to full October proactive maintenance for complete peace of mind.
          </p>
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Basic Package */}
          <div 
            className={`relative rounded-xl p-8 transition-all duration-300 cursor-pointer ${
              selectedOption === 'basic' 
                ? 'ring-2 ring-orange-300 bg-white shadow-lg' 
                : 'bg-white shadow-sm hover:shadow-md'
            }`}
            onClick={() => setSelectedOption('basic')}
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: pillBg, color: heading }}>
                <Wrench className="h-3 w-3" />
                Basic Package
              </div>
              <h3 className="text-2xl font-semibold mb-2" style={{ color: heading }}>
                Dryer Vent Cleaning
              </h3>
              <div className="text-4xl font-bold mb-2" style={{ color: accentColor }}>
                $99
              </div>
              <p className="text-sm" style={{ color: sub }}>
                One-time service
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              {basicFeatures.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
                  <span className="text-sm" style={{ color: heading }}>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-sm mb-4" style={{ color: sub }}>
                <Clock className="h-4 w-4" />
                <span>Service time: 1-2 hours</span>
              </div>
              <button 
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  selectedOption === 'basic'
                    ? 'text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                style={selectedOption === 'basic' ? { backgroundColor: accentColor } : {}}
              >
                {selectedOption === 'basic' ? 'Selected' : 'Select Basic'}
              </button>
            </div>
          </div>

          {/* Full Package */}
          <div 
            className={`relative rounded-xl p-8 transition-all duration-300 cursor-pointer ${
              selectedOption === 'full' 
                ? 'ring-2 ring-orange-300 bg-white shadow-lg' 
                : 'bg-white shadow-sm hover:shadow-md'
            }`}
            onClick={() => setSelectedOption('full')}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <div className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: accentColor, color: 'white' }}>
                Most Popular
              </div>
            </div>

            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: pillBg, color: heading }}>
                <Star className="h-3 w-3" />
                Full Package
              </div>
              <h3 className="text-2xl font-semibold mb-2" style={{ color: heading }}>
                Complete October Maintenance
              </h3>
              <div className="text-4xl font-bold mb-2" style={{ color: accentColor }}>
                $299
              </div>
              <p className="text-sm" style={{ color: sub }}>
                Includes dryer vent cleaning + full proactive care
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              {fullFeatures.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
                  <span className="text-sm" style={{ color: heading }}>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-sm mb-4" style={{ color: sub }}>
                <Clock className="h-4 w-4" />
                <span>Service time: 4-6 hours</span>
              </div>
              <button 
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  selectedOption === 'full'
                    ? 'text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                style={selectedOption === 'full' ? { backgroundColor: accentColor } : {}}
              >
                {selectedOption === 'full' ? 'Selected' : 'Select Full Package'}
              </button>
            </div>
          </div>
        </div>

        {/* Selected Package Summary */}
        <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-semibold mb-2" style={{ color: heading }}>
              {selectedOption === 'basic' ? 'Dryer Vent Cleaning' : 'Complete October Maintenance'}
            </h3>
            <div className="text-3xl font-bold mb-2" style={{ color: accentColor }}>
              {selectedOption === 'basic' ? '$99' : '$299'}
            </div>
            <p className="text-sm" style={{ color: sub }}>
              {selectedOption === 'basic' 
                ? 'Professional dryer vent cleaning service' 
                : 'Complete proactive maintenance including dryer vent cleaning'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3" style={{ color: heading }}>What's Included:</h4>
              <ul className="space-y-2">
                {(selectedOption === 'basic' ? basicFeatures : fullFeatures).map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
                    <span className="text-sm" style={{ color: sub }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ color: heading }}>Service Details:</h4>
              <div className="space-y-3 text-sm" style={{ color: sub }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Scheduled at your convenience</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{selectedOption === 'basic' ? '1-2 hours' : '4-6 hours'} service time</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  <span>Professional Hudson technicians</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  <span>Detailed service report included</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/membership" 
              className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              style={{ backgroundColor: accentColor }}
            >
              Book {selectedOption === 'basic' ? 'Dryer Vent Cleaning' : 'October Maintenance'}
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              style={{ borderColor: accentColor, color: accentColor }}
            >
              Book Your Consultation
            </a>
          </div>
          <p className="mt-4 text-sm" style={{ color: sub }}>
            Limited time offer • Book by October 31st • 30-day satisfaction guarantee
          </p>
        </div>
      </div>
    </section>
  )
}

export default OctoberFeaturedSection
